import { isAdminApiEnabled } from "../config/adminApiFeature";

const V = 2;
const K = {
  v: "vh_admin_store_v",
  contacts: "vh_admin_contacts",
  requests: "vh_admin_requests",
};

export const ADMIN_INBOX_CHANGE = "vh-admin-inbox-change";

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private mode */
  }
}

export function notifyInboxChange() {
  window.dispatchEvent(new CustomEvent(ADMIN_INBOX_CHANGE));
}

function ensureVersion() {
  const cur = localStorage.getItem(K.v);
  if (String(cur) === String(V)) return;
  localStorage.setItem(K.v, String(V));
}

export function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeAnalysis(a) {
  if (!a || typeof a !== "object") {
    return {
      status: "none",
      summary: "",
      error: "",
      updatedAt: null,
      attempts: 0,
      runs: [],
      pendingMode: null,
      pendingNote: null,
      instructionNote: "",
    };
  }
  return {
    status: a.status || "none",
    summary: String(a.summary || ""),
    error: String(a.error || ""),
    updatedAt: a.updatedAt ?? null,
    attempts: typeof a.attempts === "number" ? a.attempts : 0,
    runs: Array.isArray(a.runs) ? a.runs : [],
    pendingMode: a.pendingMode ?? null,
    pendingNote: a.pendingNote ?? null,
    instructionNote: String(a.instructionNote || ""),
  };
}

function normalizeContact(r) {
  if (!r || typeof r !== "object") return r;
  return {
    ...r,
    replies: Array.isArray(r.replies) ? r.replies : [],
    status: r.status || "new",
    seenAt: r.seenAt ?? null,
  };
}

function normalizeRequest(r) {
  if (!r || typeof r !== "object") return r;
  return {
    ...r,
    replies: Array.isArray(r.replies) ? r.replies : [],
    status: r.status || "new",
    seenAt: r.seenAt ?? null,
    aiAnalysis: normalizeAnalysis(r.aiAnalysis),
  };
}

export function getContacts() {
  ensureVersion();
  return readJson(K.contacts, []).map(normalizeContact);
}

export function appendContact(payload) {
  ensureVersion();
  const list = getContacts();
  const row = normalizeContact({
    id: uid(),
    createdAt: new Date().toISOString(),
    replies: [],
    status: "new",
    seenAt: null,
    ...payload,
  });
  list.unshift(row);
  writeJson(K.contacts, list);
  notifyInboxChange();
  return row;
}

export function updateContact(id, patch) {
  const list = getContacts();
  const i = list.findIndex((r) => r.id === id);
  if (i < 0) return null;
  list[i] = normalizeContact({
    ...list[i],
    ...patch,
    updatedAt: new Date().toISOString(),
  });
  writeJson(K.contacts, list);
  notifyInboxChange();
  return list[i];
}

export function getRequests() {
  ensureVersion();
  return readJson(K.requests, []).map(normalizeRequest);
}

export function appendServiceRequest(payload) {
  ensureVersion();
  const list = getRequests();
  const row = normalizeRequest({
    id: uid(),
    createdAt: new Date().toISOString(),
    replies: [],
    status: "new",
    seenAt: null,
    ...payload,
  });
  list.unshift(row);
  writeJson(K.requests, list);
  notifyInboxChange();
  if (typeof window !== "undefined") {
    import("./requestAnalysisRunner")
      .then(({ queueAutomaticAnalysis }) => queueAutomaticAnalysis(row.id))
      .catch(() => {});
  }
  return row;
}

/** Merge fields on `aiAnalysis` for a service request (GPT / mock pipeline). */
export function patchRequestAnalysis(id, partial) {
  const list = readJson(K.requests, []).map(normalizeRequest);
  const i = list.findIndex((r) => r.id === id);
  if (i < 0) return null;
  const cur = list[i];
  const ai = normalizeAnalysis(cur.aiAnalysis);
  const nextAi = normalizeAnalysis({ ...ai, ...partial });
  list[i] = normalizeRequest({
    ...cur,
    aiAnalysis: nextAi,
    updatedAt: new Date().toISOString(),
  });
  writeJson(K.requests, list);
  notifyInboxChange();
  return list[i];
}

export function updateRequest(id, patch) {
  const list = getRequests();
  const i = list.findIndex((r) => r.id === id);
  if (i < 0) return null;
  list[i] = normalizeRequest({
    ...list[i],
    ...patch,
    updatedAt: new Date().toISOString(),
  });
  writeJson(K.requests, list);
  notifyInboxChange();
  return list[i];
}

export function deleteRequest(id) {
  ensureVersion();
  const list = getRequests().filter((r) => r.id !== id);
  writeJson(K.requests, list);
  notifyInboxChange();
}

export function deleteRequestReply(requestId, replyId) {
  const list = getRequests();
  const i = list.findIndex((r) => r.id === requestId);
  if (i < 0) return null;
  const prev = list[i];
  const replies = (prev.replies || []).filter((rep) => rep.id !== replyId);
  list[i] = normalizeRequest({
    ...prev,
    replies,
    updatedAt: new Date().toISOString(),
  });
  writeJson(K.requests, list);
  notifyInboxChange();
  return list[i];
}

export function appendRequestReply(id, { body, author = "Admin" }) {
  const text = (body || "").trim();
  if (!text) return null;
  const list = getRequests();
  const i = list.findIndex((r) => r.id === id);
  if (i < 0) return null;
  const prev = list[i];
  const replies = [
    ...(prev.replies || []),
    {
      id: uid(),
      body: text,
      author,
      createdAt: new Date().toISOString(),
    },
  ];
  list[i] = normalizeRequest({
    ...prev,
    replies,
    updatedAt: new Date().toISOString(),
  });
  writeJson(K.requests, list);
  notifyInboxChange();
  return replies[replies.length - 1];
}

export function deleteContact(id) {
  ensureVersion();
  const list = getContacts().filter((r) => r.id !== id);
  writeJson(K.contacts, list);
  notifyInboxChange();
}

export function deleteContactReply(contactId, replyId) {
  const list = getContacts();
  const i = list.findIndex((r) => r.id === contactId);
  if (i < 0) return null;
  const prev = list[i];
  const replies = (prev.replies || []).filter((rep) => rep.id !== replyId);
  list[i] = normalizeContact({
    ...prev,
    replies,
    updatedAt: new Date().toISOString(),
  });
  writeJson(K.contacts, list);
  notifyInboxChange();
  return list[i];
}

export function appendContactReply(id, { body, author = "Admin" }) {
  const text = (body || "").trim();
  if (!text) return null;
  const list = getContacts();
  const i = list.findIndex((r) => r.id === id);
  if (i < 0) return null;
  const prev = list[i];
  const replies = [
    ...(prev.replies || []),
    {
      id: uid(),
      body: text,
      author,
      createdAt: new Date().toISOString(),
    },
  ];
  list[i] = normalizeContact({
    ...prev,
    replies,
    updatedAt: new Date().toISOString(),
  });
  writeJson(K.contacts, list);
  notifyInboxChange();
  return replies[replies.length - 1];
}

export function markRequestSeen(id) {
  const row = getRequests().find((r) => r.id === id);
  if (!row || row.seenAt) return;
  updateRequest(id, { seenAt: new Date().toISOString() });
}

export function markContactSeen(id) {
  const row = getContacts().find((r) => r.id === id);
  if (!row || row.seenAt) return;
  updateContact(id, { seenAt: new Date().toISOString() });
}

export function markAllInboxSeen() {
  ensureVersion();
  const now = new Date().toISOString();
  const reqs = getRequests().map((r) =>
    r.seenAt ? r : { ...r, seenAt: now }
  );
  const contacts = getContacts().map((c) =>
    c.seenAt ? c : { ...c, seenAt: now }
  );
  writeJson(K.requests, reqs);
  writeJson(K.contacts, contacts);
  notifyInboxChange();
}

export function getUnreadInboxCount() {
  const r = getRequests().filter((x) => !x.seenAt).length;
  const c = getContacts().filter((x) => !x.seenAt).length;
  return r + c;
}

/** Merged timeline for notifications / sidebar (newest first). */
export function getInboxActivity(limit = 30) {
  const req = getRequests().map((r) => ({
    kind: "request",
    id: r.id,
    createdAt: r.createdAt,
    typeLabel: "Service request",
    title: r.name || "—",
    subtitle: r.serviceTitle || r.email || "",
    unread: !r.seenAt,
    replyCount: (r.replies || []).length,
    requesterType: r.requesterType || "company",
    status: r.status || "new",
  }));
  const con = getContacts().map((c) => ({
    kind: "contact",
    id: c.id,
    createdAt: c.createdAt,
    typeLabel: "Contact",
    title: c.fullName || "—",
    subtitle: c.email || "",
    unread: !c.seenAt,
    replyCount: (c.replies || []).length,
    requesterType: null,
    status: c.status || "new",
  }));
  return [...req, ...con]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, limit);
}

/** Demo rows so the dashboard is usable before real messages exist. */
export function seedDemoInboxIfEmpty() {
  if (isAdminApiEnabled()) return;
  ensureVersion();
  const contacts = readJson(K.contacts, []);
  const requests = readJson(K.requests, []);
  if (contacts.length || requests.length) return;

  const now = Date.now();
  const contactId = uid();
  const analysisRun = (offsetMs, mode, ok, summary, error, note = null) => ({
    id: uid(),
    at: new Date(now + offsetMs).toISOString(),
    mode,
    ok,
    summary: ok ? summary : "",
    error: ok ? "" : error,
    note,
  });

  writeJson(K.contacts, [
    normalizeContact({
      id: contactId,
      createdAt: new Date(now - 86400000 * 2).toISOString(),
      fullName: "Sample Lead",
      email: "lead@example.com",
      phone: "+44 20 0000 0000",
      country: "UK",
      company: "Example Ltd",
      message: "We are looking for a cloud migration partner.",
      newsletter: true,
      replies: [
        {
          id: uid(),
          author: "Admin",
          body: "Thanks — we'll send a capability deck within one business day.",
          createdAt: new Date(now - 86400000 * 2 + 3600000).toISOString(),
        },
      ],
      status: "waiting_response",
      seenAt: null,
      _demo: true,
    }),
  ]);
  const alexSummary =
    "**Summary:** Alex Morgan (Northwind) is asking about **Platform & DevOps**. Key need: Kubernetes, CI/CD. **Next step:** reply with a concise scope checklist and offer a discovery call.";
  const jamieStandard =
    "**Summary:** Jamie Chen (Lumen Analytics) is asking about **Security & compliance**. Key need: SOC 2 Type II readiness, small SaaS team. **Next step:** reply with a concise scope checklist and offer a discovery call.";
  const jamieSummary =
    "**Executive read**\nContact: Jamie Chen (Lumen Analytics). Service line: **Security & compliance**.\n\n**What they are asking for**\nSOC 2 readiness assessment for a 12-person SaaS product.\n\n**Suggested next steps**\n1. Confirm trust service criteria scope.\n2. Share sample controls matrix.\n3. Schedule kickoff with security lead.";

  writeJson(K.requests, [
    normalizeRequest({
      id: uid(),
      createdAt: new Date(now - 86400000).toISOString(),
      requesterType: "company",
      name: "Alex Morgan",
      companyName: "Northwind",
      email: "alex@northwind.test",
      requirements: "Kubernetes, CI/CD",
      description: "Need help hardening staging and production pipelines.",
      serviceTitle: "Platform & DevOps",
      replies: [],
      status: "new",
      seenAt: null,
      _demo: true,
      aiAnalysis: {
        status: "ok",
        summary: alexSummary,
        error: "",
        updatedAt: new Date(now - 82000000).toISOString(),
        attempts: 1,
        runs: [analysisRun(-82000000, "auto", true, alexSummary, "")],
        pendingMode: null,
        pendingNote: null,
      },
    }),
    normalizeRequest({
      id: uid(),
      createdAt: new Date(now - 72000000).toISOString(),
      requesterType: "company",
      name: "Jamie Chen",
      companyName: "Lumen Analytics",
      email: "jamie@lumen-analytics.test",
      requirements: "SOC 2 Type II readiness, small SaaS team",
      description: "We ship B2B analytics; need a pragmatic path to SOC 2 without slowing releases.",
      serviceTitle: "Security & compliance",
      replies: [],
      status: "waiting_response",
      seenAt: null,
      _demo: true,
      aiAnalysis: {
        status: "ok",
        summary: jamieSummary,
        error: "",
        updatedAt: new Date(now - 71000000).toISOString(),
        attempts: 2,
        runs: [
          analysisRun(-71500000, "standard", true, jamieStandard, ""),
          analysisRun(-71000000, "detailed", true, jamieSummary, ""),
        ],
        pendingMode: null,
        pendingNote: null,
      },
    }),
    normalizeRequest({
      id: uid(),
      createdAt: new Date(now - 50000000).toISOString(),
      requesterType: "individual",
      name: "Priya Shah",
      companyName: "",
      email: "priya.shah@example.test",
      requirements: "Legacy mainframe integration with a new API layer",
      description: "Budget TBD. Exploring partners who have mainframe + cloud experience.",
      serviceTitle: "Architecture & integration",
      replies: [],
      status: "new",
      seenAt: null,
      _demo: true,
      aiAnalysis: {
        status: "error",
        summary: "",
        error: "OpenAI API returned 429 (rate limit). Retry shortly or switch model.",
        updatedAt: new Date(now - 49800000).toISOString(),
        attempts: 2,
        runs: [
          analysisRun(-49950000, "auto", false, "", "Network timeout calling api.openai.com — no response body."),
          analysisRun(-49800000, "standard", false, "", "OpenAI API returned 429 (rate limit). Retry shortly or switch model."),
        ],
        pendingMode: null,
        pendingNote: null,
      },
    }),
    normalizeRequest({
      id: uid(),
      createdAt: new Date(now - 12000000).toISOString(),
      requesterType: "company",
      name: "Marco Ruiz",
      companyName: "Coastal Retail",
      email: "m.ruiz@coastalretail.test",
      requirements: "POS + inventory sync to cloud data warehouse",
      description: "Multi-store rollout in Q3; need architecture review before vendor lock-in.",
      serviceTitle: "Data & analytics",
      replies: [],
      status: "new",
      seenAt: null,
      _demo: true,
      aiAnalysis: {
        status: "pending",
        summary: "",
        error: "",
        updatedAt: null,
        attempts: 0,
        runs: [],
        pendingMode: "standard",
        pendingNote: null,
      },
    }),
    normalizeRequest({
      id: uid(),
      createdAt: new Date(now - 86400000 * 42).toISOString(),
      requesterType: "company",
      name: "Elena Vogt",
      companyName: "Vogt Manufacturing",
      email: "elena@vogt-mfg.test",
      requirements: "Shop floor IoT telemetry into Azure",
      description: "First attempt at analysis failed; team retried with detailed mode and extra context.",
      serviceTitle: "Cloud & AWS",
      replies: [],
      status: "confirmed",
      seenAt: null,
      _demo: true,
      aiAnalysis: {
        status: "ok",
        summary:
          "**Executive read**\nContact: Elena Vogt (Vogt Manufacturing). Service line: **Cloud & AWS**.\n\n**What they are asking for**\nShop floor IoT telemetry into Azure.\n\n**Context**\nFirst attempt at analysis failed; team retried with detailed mode and extra context.\n\n**Suggested next steps**\n1. Map device protocols and ingestion volume.\n2. Define Azure IoT Hub vs Event Hubs tradeoffs.\n3. Propose a pilot line before plant-wide rollout.",
        error: "",
        updatedAt: new Date(now - 86400000 * 40).toISOString(),
        attempts: 2,
        runs: [
          analysisRun(-86400000 * 41, "auto", false, "", "The model failed to complete: context length exceeded after tokenization."),
          analysisRun(
            -86400000 * 40,
            "detailed",
            true,
            "**Executive read**\nContact: Elena Vogt (Vogt Manufacturing). Service line: **Cloud & AWS**.\n\n**What they are asking for**\nShop floor IoT telemetry into Azure.\n\n**Context**\nFirst attempt at analysis failed; team retried with detailed mode and extra context.\n\n**Suggested next steps**\n1. Map device protocols and ingestion volume.\n2. Define Azure IoT Hub vs Event Hubs tradeoffs.\n3. Propose a pilot line before plant-wide rollout.",
            "",
            "Please expand technical options and call out Azure-specific services."
          ),
        ],
        pendingMode: null,
        pendingNote: null,
      },
    }),
  ]);
  notifyInboxChange();
}
